import { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import * as d3 from 'd3';
import classNames  from 'classnames';

import './DonutChart.scss';

const DonutChart = ({ chartData, width, logo, height, margin, outerRadiusCompensation, innerRadius, onSegmentClick, onSegmentHover, onMouseLeave, focused, middleText, currentReportDate }) => {
    const [compHeight, setCompHeight] = useState(height);
    const [compWidth, setCompWidth] = useState(width);
    const [selectedLabel, setSelectedLabel] = useState(null);
    const [focusedSegmentId, setFocusedSegmentId] = useState(null);
    const [selectedLabelColor, setSelectedLabelColor] = useState(null);

    const radius = useMemo(() => {
        return Math.min(compWidth, compHeight) / 2;
    }, [compWidth, compHeight]);

    const arc = useMemo(() => {
        return d3.arc()
            .outerRadius(radius - margin - outerRadiusCompensation)
            .innerRadius(innerRadius);
    }, [radius]);

    const segmentsData = useMemo(() => {
        const pie = d3.pie().value(function (d) {
            return d.value.value;
        }).sort(null);
        return pie(d3.entries(chartData));

    }, [chartData]);

    useEffect(() => {
        if (chartData.length === 0) {
            setCompHeight(0);
        } else {
            setCompHeight(height);
        }
    }, [height, chartData]);

    useEffect(() => {
        if (chartData.length === 0) {
            setCompWidth(0);
        } else {
            setCompWidth(width);
        }
    }, [width, chartData]);

    useEffect(() => {
        if (!focused) {
            setFocusedSegmentId(null);
            middleText ? setSelectedLabel(middleText) : setSelectedLabel(null);
        }

        segmentsData.map((data) => {
            if (data.data.value.label === focused) {
                setFocusedSegmentId(data.data.key);
                setSelectedLabelColor(data.data.value.color);
                setSelectedLabel(data.data.value.label + ' ' + data.data.value.value);
                return;
            }
        });
    }, [focused]);

    const onSegmentClicked = (data) => {
        setSelectedLabel(data.value.label + ' ' + data.value.value);
        onSegmentClick(data.value);
    };

    const onSegmentHovered = (data) => {
        setSelectedLabel(data.value.label + ' ' + data.value.value);
        setSelectedLabelColor(data.value.color);
        setFocusedSegmentId(data.key);
        onSegmentHover(data.value);
    };

    const onMouseExit = () => {
        middleText ? setSelectedLabel(middleText) : setSelectedLabel(null);
        setFocusedSegmentId(null);
        onMouseLeave(null);
    };

    const segments = useMemo(() => {
        return segmentsData.map((data, index) => {
            const colorsOfSegments = data.data.value.color;

            return <path onClick={onSegmentClicked.bind(null, data.data)}
                onMouseOver={onSegmentHovered.bind(null, data.data)}
                onMouseLeave={onMouseExit}
                className={focusedSegmentId === data.data.key ? 'focused' : ''}
                d={arc(data)}
                fill={colorsOfSegments}
                stroke='white'
                key={index}>

            </path>;
        });
    }, [arc, segmentsData, focusedSegmentId]);

    // divide the label into an array so we can split the words on different rows
    const selectedLabelSplit = selectedLabel?.split(' ');
    const getDy = (index) => {
        if (index === selectedLabelSplit.length - 1) {
            return '30px';
        }

        return index !== 0 ? '22px' : '0px';
    };
    return (
        <svg
            className="donutComponent"
            width={compWidth}
            height={compHeight}
        >
            <g transform={`translate(${compWidth / 2}, ${compHeight / 2})`}>
                {segments}
                {selectedLabel !== null
                    // if the title is on more than one row, we split it and use the y property to center vertically
                    ? <text className="selectedLabel noDate" y={`${-1 * (selectedLabel?.split(' ').length - 1) * 10}px`}>
                        {selectedLabelSplit.map((label, index) => {
                            const tspanClassName = classNames({
                                'value': !Number.isNaN(label) && index === selectedLabelSplit.length - 1
                            });

                            return <tspan
                                style={{ fill: `${index === selectedLabelSplit.length - 1 ? selectedLabelColor : 'inherit'}` }}
                                key={index}
                                x="0"
                                dy={getDy(index)}
                                className={tspanClassName}
                            >
                                {!Number.isNaN(label) && index === selectedLabelSplit.length - 1 ? label + '%' : label}
                            </tspan>;
                        })}
                    </text>
                    :
                    <>
                        <text className="selectedLabel" y="25">
                            {currentReportDate}
                        </text>
                        <image href={logo} x="-65" y="-75" height="130px" width="130px" />
                    </>
                }
            </g>
        </svg>
    );
};

DonutChart.defaultProps = {
    outerRadiusCompensation: 40,
    innerRadius: 90
};

DonutChart.propTypes = {
    chartData: PropTypes.array.isRequired,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
    margin: PropTypes.number.isRequired,
    outerRadiusCompensation: PropTypes.number,
    innerRadius: PropTypes.number,
    onSegmentClick: PropTypes.func,
    onSegmentHover: PropTypes.func,
    onMouseLeave: PropTypes.func,
    focused: PropTypes.string,
    middleText: PropTypes.string,
    currentReportDate: PropTypes.string,
    logo: PropTypes.string
};

export default DonutChart;
